import { useState } from 'react';
import {
  useGetCategories,
  useGetCategoriesByTheme,
  useGetThemes,
  useReorderCategories,
} from '@/api/glow-up/glow-up.hooks';

import { ErrorScreen } from '@/components/ui/error-screen';
import { FilterSelect } from '@/components/ui/filter-select';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { SortableList } from '@/components/ui/sortable-list';
import { CategoryCard } from './CategoryCard';

type ListProps = {
  refetch: () => void;
};

export const CategoryList = ({ refetch }: ListProps) => {
  const [themeId, setThemeId] = useState<number | null>(null);

  const { themes } = useGetThemes({ skip: 0, take: 1000 });
  const {
    categories: allCategories,
    isLoading: allLoading,
    isError: allError,
  } = useGetCategories({ skip: 0, take: 1000 });
  const {
    categories: filteredCategories,
    isLoading: filteredLoading,
    isError: filteredError,
  } = useGetCategoriesByTheme(themeId ?? 0);

  const reorder = useReorderCategories();

  const isLoading = themeId !== null ? filteredLoading : allLoading;
  const isError = themeId !== null ? filteredError : allError;
  const categories =
    themeId !== null ? (filteredCategories ?? []) : (allCategories ?? []);

  const themeOptions = (themes ?? []).map((t) => ({
    id: t.id,
    label: t.title,
  }));

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto pb-12">
      <FilterSelect
        value={themeId}
        onChange={setThemeId}
        options={themeOptions}
        placeholder="Tous les thèmes"
      />
      {isLoading && <LoadingScreen />}
      {!isLoading && isError && <ErrorScreen />}
      {!isLoading && !isError && categories.length === 0 && (
        <p className="text-center text-muted-foreground py-8">
          Aucune catégorie pour le moment.
        </p>
      )}
      {!isLoading && !isError && categories.length > 0 && (
        <SortableList
          items={categories}
          renderItem={(category) => (
            <div className="flex flex-wrap gap-4 py-1">
              <CategoryCard category={category} refetch={refetch} />
            </div>
          )}
          onReorder={(items) => reorder.mutate(items)}
        />
      )}
    </div>
  );
};
